import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CommonModal from '../../Components/CommonModal';
import IssueCountsModal from '../../Dashboard/ExposureMap/IssueCountsModal';
import CommonTabs from '../../Components/CommonTabs';
import CommonTable from '../../Components/CommonTable';
import CommonButton from '../../Components/CommonButton';
import CommonDropdown from '../../Components/CommonDropdown';
import CommonInput from '../../Components/CommonInput';
import CommonDatePicker from '../../Components/CommonDatePicker';
import ButtonLoader from '../../Components/ButtonLoader';
import StatusBadge from '../../Components/StatusBadge';
import {
    show_vendors_details_data,
    download_cxo_report,
    get_questionnaires,
    createTprmAssessmentService,
    edit_by_id_third_party,
} from '../../services';
import {
    criticalityTone, formatDate, gradeTone, MISSING_TEXT, severityTone, vendorGradeFallback,
    SERVICE_OPTIONS, CATEGORY_OPTIONS, YES_NO_OPTIONS,
} from '../tpcrmUtils';
import '../../styles/TpcrmPortal.scss';

const TABS = ['Overview', 'Scan Report', 'Assessment'];
const TAB_VALUES = ['overview', 'scan_report', 'assessment'];

// CommonInput renders ~50px tall (common.scss's 12px vertical padding, no
// fixed height) vs. CommonDropdown's fixed 38px — visible row-to-row in this
// form since text fields (Vendor Name/Domain/Email/Internal Owner) and
// dropdowns (Service Provided, Vendor Category, the 7 Yes/No fields) sit side
// by side. Same fixed-height fix already used in CreateTPRMQuestionModal.js/
// BulkUploadQuestionsModal.js's custom-mode fields.
const COMPACT_INPUT_STYLE = { height: 38, padding: '0 12px' };

const yesNo = (value) => (value ? 'Yes' : 'No');

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function ReadField({ label, children }) {
    return (
        <div>
            <span className="tpcrm-profile-label">{label}</span>
            <span>{children}</span>
        </div>
    );
}

function TextField({ label, required, value, onChange, error, type = 'text', maxLength }) {
    return (
        <div>
            <span className="tpcrm-profile-label">{label}{required && ' *'}</span>
            <CommonInput type={type} value={value} maxLength={maxLength} onChange={(e) => onChange(e.target.value)} style={COMPACT_INPUT_STYLE} />
            {error && <small className="tpcrm-profile-field-err">{error}</small>}
        </div>
    );
}

function SelectField({ label, value, onChange, options }) {
    return (
        <div>
            <span className="tpcrm-profile-label">{label}</span>
            <CommonDropdown value={value} onChange={onChange} options={options} minWidth={0} style={{ width: '100%' }} />
        </div>
    );
}

// Unified vendor profile — replaces 3 of the original dashboard's separate
// modals (vendor edit, ShowVendorsDetailsData scan report, MonitoredVendors'
// inline send-questionnaire cell) with one tabbed modal opened from a single
// click on the vendor's name. The Overview tab edits inline (no nested
// modal) since all the vendor's data is already on screen here — see
// TPCRM-DASHBOARD-REFERENCE.md for the original per-feature breakdown this
// consolidates.
function VendorProfileModal({ show, onHide, vendor, onUpdated }) {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('overview');
    const [localVendor, setLocalVendor] = useState(vendor);

    const [isEditingOverview, setIsEditingOverview] = useState(false);
    const [editForm, setEditForm] = useState(null);
    const [editErrors, setEditErrors] = useState({});
    const [editSaving, setEditSaving] = useState(false);
    const [editMsg, setEditMsg] = useState('');

    const [cxoBusy, setCxoBusy] = useState(false);
    const [cxoError, setCxoError] = useState('');

    const [scanRows, setScanRows] = useState([]);
    // Row whose observations are open. IssueCountsModal reads the evidence
    // straight off the row (props.evidencelist.evidence_list) and fetches
    // nothing, so the vendor's own findings render without any new endpoint.
    const [observationsRow, setObservationsRow] = useState(null);
    const [scanLoading, setScanLoading] = useState(false);
    const [sortBy, setSortBy] = useState('');

    const [questionnaireOptions, setQuestionnaireOptions] = useState([{ value: '', label: 'Default Questionnaire' }]);
    const [selectedQuestionnaire, setSelectedQuestionnaire] = useState('');
    const [expiryDate, setExpiryDate] = useState('');
    const [sending, setSending] = useState(false);
    const [assessmentMsg, setAssessmentMsg] = useState('');

    const targetOrgId = vendor?.target_organization_id;
    const vendorRelationId = vendor?.id || vendor?.relation_id;
    const vendorGrade = vendorGradeFallback(localVendor);
    const hasAssessment = Boolean(localVendor?.latest_assessment?.questionnaire_display);
    // Whether that assessment is still live. `expires_at` is the only date this
    // payload carries (there is no `status` on `latest_assessment` — see the
    // field audit), so expiry is what distinguishes "a questionnaire is out
    // with the vendor right now" from "the last one is finished with".
    const currentExpiry = localVendor?.latest_assessment?.expires_at;
    const expiryTime = currentExpiry ? new Date(currentExpiry).getTime() : NaN;
    const assessmentStillOpen = Number.isFinite(expiryTime) && expiryTime >= Date.now();

    useEffect(() => {
        if (!show) return;
        setLocalVendor(vendor);
        setActiveTab('overview');
        setIsEditingOverview(false);
        setEditMsg('');
        setCxoError('');
        setAssessmentMsg('');
        setSelectedQuestionnaire('');
        setExpiryDate('');
    }, [show, vendor]);

    useEffect(() => {
        if (!show || activeTab !== 'scan_report' || !targetOrgId) return;
        let cancelled = false;
        const fetchScan = async () => {
            setScanLoading(true);
            try {
                const response = await show_vendors_details_data({ target_organization_id: targetOrgId, sort_by: sortBy });
                if (!cancelled) setScanRows(response?.statusCode?.response_data?.data || []);
            } catch (error) {
                console.error('Failed to load vendor scan report:', error);
                if (!cancelled) setScanRows([]);
            } finally {
                if (!cancelled) setScanLoading(false);
            }
        };
        fetchScan();
        return () => { cancelled = true; };
    }, [show, activeTab, targetOrgId, sortBy]);

    // No `hasAssessment` guard: the send form renders for vendors that already
    // have an assessment now, so skipping this fetch for them would leave their
    // questionnaire dropdown holding nothing but "Default Questionnaire".
    useEffect(() => {
        if (!show || activeTab !== 'assessment') return;
        let cancelled = false;
        const fetchQuestionnaires = async () => {
            try {
                const response = await get_questionnaires();
                const list = response?.statusCode?.response_data?.data?.questionnaires || [];
                if (!cancelled) {
                    setQuestionnaireOptions([
                        { value: '', label: 'Default Questionnaire' },
                        ...list.map((q) => ({ value: q.name, label: q.name })),
                    ]);
                }
            } catch (error) {
                console.error('Failed to load questionnaires:', error);
            }
        };
        fetchQuestionnaires();
        return () => { cancelled = true; };
    }, [show, activeTab]);

    const handleCxoDownload = async () => {
        if (!targetOrgId) return;
        setCxoBusy(true);
        setCxoError('');
        try {
            const response = await download_cxo_report(targetOrgId);
            const blob = response?.statusCode;
            if (blob?.type === 'application/json') {
                const text = await blob.text();
                const errorData = JSON.parse(text);
                setCxoError(errorData?.error?.message || 'CXO report is not available for this vendor yet.');
                return;
            }
            const fileBlob = new Blob([blob], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(fileBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${localVendor?.vendor_name || 'vendor'}-cxo-report.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('CXO report download failed:', error);
            setCxoError('Unable to download the CXO report.');
        } finally {
            setCxoBusy(false);
        }
    };

    const calculateExpiryDays = () => {
        if (!expiryDate) return 15;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const target = new Date(expiryDate);
        target.setHours(0, 0, 0, 0);
        return Math.max(1, Math.ceil((target - today) / 86400000));
    };

    const handleSendQuestionnaire = async () => {
        if (!expiryDate) {
            setAssessmentMsg('Please select an expiry date.');
            return;
        }
        setSending(true);
        setAssessmentMsg('');
        try {
            const response = await createTprmAssessmentService({
                relation_id: parseInt(vendorRelationId, 10),
                questionnaire_name: selectedQuestionnaire || '',
                expiry_days: calculateExpiryDays(),
                send_immediately: true,
            });
            const message = response?.statusCode?.response_data?.message || response?.statusCode?.message;
            if (message) {
                setAssessmentMsg(message);
                onUpdated?.();
            } else {
                setAssessmentMsg(
                    response?.errors?.message || response?.error?.message
                    || response?.statusCode?.error?.message || 'Failed to send questionnaire.',
                );
            }
        } catch (error) {
            console.error('Send questionnaire failed:', error);
            setAssessmentMsg('Failed to send questionnaire.');
        } finally {
            setSending(false);
        }
    };

    const handleEditClick = () => {
        setEditForm({
            vendorName: localVendor?.vendor_name || '',
            vendorDomain: localVendor?.vendor_domain || localVendor?.primary_domain || '',
            vendorEmail: localVendor?.vendor_email || '',
            serviceProvided: localVendor?.service_provided || '',
            credentialAccess: !!localVendor?.credential_access,
            customerPersonalData: !!localVendor?.customer_personal_data,
            customerSensitivePersonalData: !!localVendor?.customer_sensitive_personal_data,
            customerFinancialData: !!localVendor?.customer_financial_data,
            customerHealthData: !!localVendor?.customer_health_data,
            customerAuthData: !!localVendor?.customer_auth_data,
            businessConfidentialData: !!localVendor?.business_confidential_data,
            vendorCategory: localVendor?.vendor_category || 'Auto',
            internalOwner: localVendor?.internal_owner || '',
        });
        setEditErrors({});
        setEditMsg('');
        setIsEditingOverview(true);
    };

    const updateEditField = (field, value) => {
        setEditForm((prev) => ({ ...prev, [field]: value }));
        setEditErrors((prev) => ({ ...prev, [field]: '' }));
    };

    const validateEdit = () => {
        const errors = {};
        if (!editForm.vendorName.trim()) errors.vendorName = 'Vendor name is required.';
        else if (editForm.vendorName.length > 255) errors.vendorName = 'Vendor name must be less than 255 characters.';

        if (!editForm.vendorDomain.trim()) errors.vendorDomain = 'Vendor domain is required.';
        else if (editForm.vendorDomain.length > 255) errors.vendorDomain = 'Vendor domain must be less than 255 characters.';

        if (!editForm.vendorEmail.trim()) errors.vendorEmail = 'Vendor email is required.';
        else if (!EMAIL_REGEX.test(editForm.vendorEmail)) errors.vendorEmail = 'Please enter a valid email address.';

        if (!editForm.serviceProvided.trim()) errors.serviceProvided = 'Service provided is required.';

        if (editForm.internalOwner.trim() && !EMAIL_REGEX.test(editForm.internalOwner)) {
            errors.internalOwner = 'Please enter a valid internal owner email address.';
        }

        setEditErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSaveEdit = async () => {
        if (!validateEdit()) return;
        setEditSaving(true);
        setEditMsg('');
        const rawParams = {
            vendor_name: editForm.vendorName.trim(),
            vendor_domain: editForm.vendorDomain.trim(),
            vendor_email: editForm.vendorEmail.trim(),
            service_provided: editForm.serviceProvided.trim(),
            credential_access: editForm.credentialAccess,
            customer_personal_data: editForm.customerPersonalData,
            customer_sensitive_personal_data: editForm.customerSensitivePersonalData,
            customer_financial_data: editForm.customerFinancialData,
            customer_health_data: editForm.customerHealthData,
            customer_auth_data: editForm.customerAuthData,
            business_confidential_data: editForm.businessConfidentialData,
            vendor_category: editForm.vendorCategory === 'Auto' ? null : editForm.vendorCategory || null,
            internal_owner: editForm.internalOwner.trim() || null,
        };
        // The edit endpoint rejects explicit nulls with "This field may not be
        // null." (the create endpoint accepts them, which is why Add Vendor
        // works and Save Changes did not). A "Auto" category or a blank
        // internal owner produced null above, so drop unset keys instead of
        // sending them — the field is then simply left as-is server-side.
        const params = Object.fromEntries(
            Object.entries(rawParams).filter(([, value]) => value !== null && value !== undefined)
        );
        try {
            const response = await edit_by_id_third_party({ id: targetOrgId, params });
            const successMessage = response?.statusCode?.response_data?.message;
            if (successMessage) {
                setLocalVendor((prev) => ({ ...prev, ...params }));
                setIsEditingOverview(false);
                onUpdated?.();
            } else {
                setEditMsg(response?.statusCode?.error?.message || 'Failed to update vendor.');
            }
        } catch (error) {
            console.error('Failed to update vendor:', error);
            setEditMsg('Something went wrong, please try again.');
        } finally {
            setEditSaving(false);
        }
    };

    if (!vendor) return null;

    const scanHeaders = [
        'Category',
        'Finding',
        <span key="issue_count" className="tpcrm-sortable-th" onClick={() => setSortBy('issue_count')}>Issue Count ↕</span>,
        <span key="severity" className="tpcrm-sortable-th" onClick={() => setSortBy('severity')}>Severity ↕</span>,
        'MITRE | ATT&CK',
    ];

    // Only offer the drill-down when the row actually carries evidence —
    // otherwise the modal would open onto an empty table.
    const hasObservations = (row) => {
        const list = row?.evidence_list;
        if (!list) return false;
        if (typeof list === 'string') {
            const t = list.trim();
            return t !== '' && t !== '[]' && t !== 'null';
        }
        return Array.isArray(list) ? list.length > 0 : true;
    };

    const scanTableRows = scanRows.map((item, index) => ({
        data: [
            item?.category || <span className="tpcrm-cell-empty">{MISSING_TEXT.category}</span>,
            item?.finding || <span className="tpcrm-cell-empty">{MISSING_TEXT.finding}</span>,
            hasObservations(item) ? (
                <button
                    key={`count-${index}`}
                    type="button"
                    className="tpcrm-issue-count-link"
                    onClick={() => setObservationsRow(item)}
                    title="View observations"
                >
                    {item?.issue_count ?? MISSING_TEXT.count}
                </button>
            ) : (item?.issue_count ?? <span className="tpcrm-cell-empty">{MISSING_TEXT.count}</span>),
            item?.severity
                ? <StatusBadge key={`sev-${index}`} dot={false} style={severityTone(item.severity)}>{item.severity}</StatusBadge>
                : <span key={`sev-${index}`} className="tpcrm-cell-empty">{MISSING_TEXT.severity}</span>,
            item?.mitre_tag ? (
                // Same target ExposureMap.js builds for its "MITRE | ATT&CK"
                // column: the technique id with dots as path separators
                // (T1059.007 -> techniques/T1059/007/).
                <a
                    key={`mitre-${index}`}
                    className="tpcrm-mitre-link"
                    href={`https://attack.mitre.org/techniques/${String(item.mitre_tag).split('.').join('/')}/`}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="View on MITRE ATT&CK"
                >
                    {item.mitre_tag}
                </a>
            ) : <span className="tpcrm-cell-empty">{MISSING_TEXT.mitre}</span>,
        ],
    }));

    return (
        <>
        <CommonModal show={show} onHide={onHide} title={localVendor?.vendor_name || 'Vendor'} size="xl">
            <div className="tpcrm-profile-modal-body">
                <div className="tpcrm-profile-tabsrow">
                    <CommonTabs
                        tabs={TABS}
                        values={TAB_VALUES}
                        active={activeTab}
                        handlers={[
                            () => setActiveTab('overview'),
                            () => setActiveTab('scan_report'),
                            () => setActiveTab('assessment'),
                        ]}
                    />

                    {activeTab === 'overview' && (
                        <div className="tpcrm-profile-actions">
                            {isEditingOverview ? (
                                <>
                                    <CommonButton type="button" text={editSaving ? 'Saving...' : 'Save Changes'} variant="success" className="tpcrm-row-btn" onClick={handleSaveEdit} disable={editSaving} />
                                    <CommonButton type="button" text="Cancel" className="tpcrm-row-btn" onClick={() => setIsEditingOverview(false)} disable={editSaving} />
                                </>
                            ) : (
                                <CommonButton type="button" text="Edit Vendor" variant="edit" className="tpcrm-row-btn" onClick={handleEditClick} />
                            )}
                        </div>
                    )}

                    {/* No scan data → no report to download, so the button is hidden
                        entirely rather than left disabled. */}
                    {activeTab === 'scan_report' && scanRows.length > 0 && (
                        <div className="tpcrm-profile-actions">
                            <CommonButton
                                type="button"
                                text={cxoBusy ? 'Downloading...' : 'Download CXO Report'}
                                className="tpcrm-row-btn"
                                onClick={handleCxoDownload}
                                disable={cxoBusy || !vendorGrade || vendorGrade === '-'}
                            />
                        </div>
                    )}

                    {activeTab === 'assessment' && (
                        <div className="tpcrm-profile-actions">
                            <CommonButton
                                type="button"
                                text="View Response"
                                className="tpcrm-row-btn"
                                onClick={() => navigate(`/vendor/assessment/${localVendor?.latest_assessment?.id}/responses`)}
                                disable={!hasAssessment || !localVendor?.latest_assessment?.id}
                            />
                        </div>
                    )}
                </div>

                <div className="tpcrm-profile-tabcontent">
                    {activeTab === 'overview' && (
                        <div className="tpcrm-profile-pane">
                            {editMsg && <div className="tpcrm-profile-msg tpcrm-profile-msg--error">{editMsg}</div>}

                            {isEditingOverview ? (
                                <div className="tpcrm-profile-columns">
                                    <div className="tpcrm-profile-section">
                                        <div className="tpcrm-profile-section-title">Vendor Profile</div>
                                        <div className="tpcrm-profile-grid">
                                            <TextField label="Vendor Name" required value={editForm.vendorName} maxLength={255} error={editErrors.vendorName}
                                                onChange={(v) => updateEditField('vendorName', v.replace(/[^a-zA-Z0-9\s]/g, ''))} />
                                            <TextField label="Vendor Domain" required value={editForm.vendorDomain} maxLength={255} error={editErrors.vendorDomain}
                                                onChange={(v) => updateEditField('vendorDomain', v)} />
                                            <TextField label="Vendor Email" required type="email" value={editForm.vendorEmail} error={editErrors.vendorEmail}
                                                onChange={(v) => updateEditField('vendorEmail', v)} />
                                            <SelectField label="Service Provided" value={editForm.serviceProvided} options={SERVICE_OPTIONS}
                                                onChange={(v) => updateEditField('serviceProvided', v)} />
                                            <ReadField label="Business Criticality">
                                                <StatusBadge dot={false} style={criticalityTone(localVendor?.business_criticality)}>{localVendor?.business_criticality || MISSING_TEXT.criticality}</StatusBadge>
                                            </ReadField>
                                            <ReadField label="Vendor Grade">
                                                <span className="tpcrm-grade-text" style={{ color: gradeTone(vendorGrade) }}>{vendorGrade}</span>
                                            </ReadField>
                                            <SelectField label="Vendor Category" value={editForm.vendorCategory} options={CATEGORY_OPTIONS}
                                                onChange={(v) => updateEditField('vendorCategory', v)} />
                                            <TextField label="Internal Owner" type="email" value={editForm.internalOwner} error={editErrors.internalOwner}
                                                onChange={(v) => updateEditField('internalOwner', v)} />
                                        </div>
                                    </div>

                                    <div className="tpcrm-profile-section">
                                        <div className="tpcrm-profile-section-title">Data Access</div>
                                        <div className="tpcrm-profile-grid">
                                            <SelectField label="Credential Access" value={editForm.credentialAccess} options={YES_NO_OPTIONS}
                                                onChange={(v) => updateEditField('credentialAccess', v)} />
                                            <SelectField label="Customer Personal Data" value={editForm.customerPersonalData} options={YES_NO_OPTIONS}
                                                onChange={(v) => updateEditField('customerPersonalData', v)} />
                                            <SelectField label="Customer Sensitive Personal Data" value={editForm.customerSensitivePersonalData} options={YES_NO_OPTIONS}
                                                onChange={(v) => updateEditField('customerSensitivePersonalData', v)} />
                                            <SelectField label="Customer Financial Data" value={editForm.customerFinancialData} options={YES_NO_OPTIONS}
                                                onChange={(v) => updateEditField('customerFinancialData', v)} />
                                            <SelectField label="Customer Health Data" value={editForm.customerHealthData} options={YES_NO_OPTIONS}
                                                onChange={(v) => updateEditField('customerHealthData', v)} />
                                            <SelectField label="Customer Auth Data" value={editForm.customerAuthData} options={YES_NO_OPTIONS}
                                                onChange={(v) => updateEditField('customerAuthData', v)} />
                                            <SelectField label="Business Confidential Data" value={editForm.businessConfidentialData} options={YES_NO_OPTIONS}
                                                onChange={(v) => updateEditField('businessConfidentialData', v)} />
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="tpcrm-profile-columns">
                                    <div className="tpcrm-profile-section">
                                        <div className="tpcrm-profile-section-title">Vendor Profile</div>
                                        <div className="tpcrm-profile-grid">
                                            <ReadField label="Vendor Domain">{localVendor?.primary_domain || localVendor?.vendor_domain
                                                || <span className="tpcrm-cell-empty">Not specified</span>}</ReadField>
                                            <ReadField label="Vendor Email">{localVendor?.vendor_email
                                                || <span className="tpcrm-cell-empty">Not specified</span>}</ReadField>
                                            <ReadField label="Service Provided">{localVendor?.service_provided
                                                || <span className="tpcrm-cell-empty">{MISSING_TEXT.service}</span>}</ReadField>
                                            <ReadField label="Business Criticality">
                                                <StatusBadge dot={false} style={criticalityTone(localVendor?.business_criticality)}>{localVendor?.business_criticality || MISSING_TEXT.criticality}</StatusBadge>
                                            </ReadField>
                                            <ReadField label="Vendor Grade">
                                                <span className="tpcrm-grade-text" style={{ color: gradeTone(vendorGrade) }}>{vendorGrade}</span>
                                            </ReadField>
                                            <ReadField label="Vendor Category">{localVendor?.vendor_category || 'Auto'}</ReadField>
                                            <ReadField label="Internal Owner">{localVendor?.internal_owner
                                                || <span className="tpcrm-cell-empty">Not assigned</span>}</ReadField>
                                        </div>
                                    </div>

                                    <div className="tpcrm-profile-section">
                                        <div className="tpcrm-profile-section-title">Data Access</div>
                                        <div className="tpcrm-profile-grid">
                                            <ReadField label="Credential Access"><StatusBadge variant={localVendor?.credential_access ? 'warning' : 'neutral'}>{yesNo(localVendor?.credential_access)}</StatusBadge></ReadField>
                                            <ReadField label="Customer Personal Data"><StatusBadge variant={localVendor?.customer_personal_data ? 'warning' : 'neutral'}>{yesNo(localVendor?.customer_personal_data)}</StatusBadge></ReadField>
                                            <ReadField label="Customer Sensitive Personal Data"><StatusBadge variant={localVendor?.customer_sensitive_personal_data ? 'warning' : 'neutral'}>{yesNo(localVendor?.customer_sensitive_personal_data)}</StatusBadge></ReadField>
                                            <ReadField label="Customer Financial Data"><StatusBadge variant={localVendor?.customer_financial_data ? 'warning' : 'neutral'}>{yesNo(localVendor?.customer_financial_data)}</StatusBadge></ReadField>
                                            <ReadField label="Customer Health Data"><StatusBadge variant={localVendor?.customer_health_data ? 'warning' : 'neutral'}>{yesNo(localVendor?.customer_health_data)}</StatusBadge></ReadField>
                                            <ReadField label="Customer Auth Data"><StatusBadge variant={localVendor?.customer_auth_data ? 'warning' : 'neutral'}>{yesNo(localVendor?.customer_auth_data)}</StatusBadge></ReadField>
                                            <ReadField label="Business Confidential Data"><StatusBadge variant={localVendor?.business_confidential_data ? 'warning' : 'neutral'}>{yesNo(localVendor?.business_confidential_data)}</StatusBadge></ReadField>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'scan_report' && (
                        <div className="tpcrm-profile-pane">
                            {cxoError && <div className="tpcrm-profile-msg tpcrm-profile-msg--error">{cxoError}</div>}
                            {scanLoading ? (
                                <div className="tpcrm-loader"><ButtonLoader /></div>
                            ) : scanTableRows.length ? (
                                <div className="tpcrm-profile-table-wrap">
                                    <CommonTable headers={scanHeaders} rows={scanTableRows} noPadding noScroll />
                                </div>
                            ) : (
                                <div className="tpcrm-profile-empty">
                                    <div className="tpcrm-profile-empty-icon">
                                        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M9 3H5a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9l-6-6H9z" />
                                            <path d="M13 3v6h6" />
                                            <path d="M9 13h6M9 17h4" />
                                        </svg>
                                    </div>
                                    <div className="tpcrm-profile-empty-title">No scan data available</div>
                                    <p className="tpcrm-profile-empty-hint">
                                        This vendor hasn't been scanned yet, or the latest scan didn't surface any issues.
                                        Findings will appear here once a scan report is available.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'assessment' && (
                        <div className="tpcrm-profile-pane">
                            {/* The summary and the send form used to be an either/or on
                                `hasAssessment`, which meant that once a vendor had ANY
                                assessment the send form was never rendered again — so an
                                expired or rejected assessment was a permanent dead end with
                                no way to re-assess the vendor from the UI. (The legacy
                                MonitoredVendors.js table has the same dead end, so this was
                                inherited rather than introduced here.) Both now render: the
                                current assessment stays on top as reference, the send form
                                sits below it. */}
                            {hasAssessment && (
                                <div className="tpcrm-profile-section">
                                    <div className="tpcrm-profile-section-title">Current Assessment</div>
                                    <div className="tpcrm-profile-grid">
                                        <ReadField label="Questionnaire">{localVendor.latest_assessment.questionnaire_display}</ReadField>
                                        <ReadField label="Current Assessment Grade">{localVendor?.current_assessment_grade
                                            || <span className="tpcrm-cell-empty">{MISSING_TEXT.grade}</span>}</ReadField>
                                        <ReadField label="Expires">{formatDate(localVendor.latest_assessment.expires_at)}</ReadField>
                                        <ReadField label="Next Scheduled">{formatDate(localVendor?.next_assessment_scheduled_date)}</ReadField>
                                    </div>
                                </div>
                            )}

                            <div className="tpcrm-profile-section">
                                <div className="tpcrm-profile-section-title">
                                    {hasAssessment ? 'Send a New Questionnaire' : 'Send Questionnaire'}
                                </div>
                                <div className="tpcrm-profile-send-body">
                                    <p className="tpcrm-profile-send-desc">
                                        {!hasAssessment
                                            ? "This vendor hasn't been sent a security questionnaire yet. Pick a questionnaire and an expiry date to start their assessment — they'll get an email with a link to complete it."
                                            : assessmentStillOpen
                                                ? `A questionnaire is already open with this vendor until ${formatDate(currentExpiry)}. Sending another one starts a fresh assessment alongside it — only do this if you mean to replace the current request.`
                                                : 'The assessment above has run its course. Pick a questionnaire and an expiry date to start a new one — the vendor gets a fresh email with a link to complete it.'}
                                    </p>
                                    <div className="tpcrm-profile-send-row">
                                        <div className="tpcrm-profile-send-field">
                                            <label className="tpcrm-profile-label">Questionnaire</label>
                                            <CommonDropdown
                                                ariaLabel="Questionnaire"
                                                value={selectedQuestionnaire}
                                                onChange={setSelectedQuestionnaire}
                                                options={questionnaireOptions}
                                                minWidth={0}
                                                style={{ width: '100%' }}
                                            />
                                        </div>
                                        <div className="tpcrm-profile-send-field">
                                            <label className="tpcrm-profile-label">Expiry Date</label>
                                            <CommonDatePicker
                                                value={expiryDate}
                                                onChange={setExpiryDate}
                                                minDate={new Date().toISOString().slice(0, 10)}
                                                placeholder="Expiry date"
                                                disabled={sending}
                                            />
                                        </div>
                                        <CommonButton
                                            type="button"
                                            text={sending ? 'Sending...' : 'Send Questionnaire'}
                                            variant="success"
                                            className="tpcrm-row-btn tpcrm-profile-send-btn"
                                            onClick={handleSendQuestionnaire}
                                            disable={sending}
                                        />
                                    </div>
                                    {assessmentMsg && <div className="tpcrm-profile-msg">{assessmentMsg}</div>}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </CommonModal>

        {observationsRow && (
            <IssueCountsModal
                show={!!observationsRow}
                onHide={() => setObservationsRow(null)}
                evidencelist={observationsRow}
                allowTakedown={false}
            />
        )}
        </>
    );
}

export default VendorProfileModal;
